import notifiers from "global/constants/NotificationConstants";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import { jwtDecode } from "jwt-decode";
import { clearAuthUserIdFromStorage } from "screens/Blogs/blogsUserPreferences";
import { navigateToHome } from "utils/navigation";
import { store } from "utils/store";
import { logOutAction, updateAccessToken } from "../redux/authSlice";

// Checks if the error code is 401 or 403 -> Logout the user
export const checkStatus = (error: any) => {
  if (error.status === 401) {
    store.dispatch(logOutAction({}));
    clearAuthUserIdFromStorage();
    navigateToHome();
    return true;
  } else return false;
};

// Authenticated Call Headers
export const getCallParams = async (methodType: string, body?: any) => {
  const accessToken = "Bearer " + store.getState().auth.accessToken;
  return {
    method: methodType,
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
    body: JSON.stringify(body),
  };
};

export const getAuthCallParams = async (
  token: string,
  methodType: string,
  body?: any,
) => {
  const accessToken = "Bearer " + token;
  return {
    method: methodType,
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
    body: JSON.stringify(body),
  };
};

//Authenticated search Headers
export async function getSearchedCallParams(methodType: string, body?: any) {
  const accessToken = store.getState().auth.accessToken;
  return {
    method: methodType,
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
    body: body,
  };
}

// Unauthenticated Call Headers
export const getNoAuthCallParams = (methodType: string, body?: any) => {
  const params: any = {
    method: methodType,
    headers: strings.applicationJSON,
  };
  if (methodType !== "GET" && body !== undefined) {
    params.body = JSON.stringify(body);
  }
  return params;
};

export async function getAccessToken(
  methodType: string,
  body?: any,
  token?: string,
) {
  const accessToken = token!;
  return {
    method: methodType,
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
    body: JSON.stringify(body),
  };
}

export const getFileCallParams = async (body: any) => {
  const accessToken = store.getState().auth.accessToken;
  return {
    method: "POST",
    headers: await getHeaderObject(accessToken, strings.multipartForm),
    body: body,
  };
};

export const getDeleteCallParams = async (body: any = {}) => {
  const accessToken = store.getState().auth.accessToken;
  return {
    method: "POST",
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
    body: JSON.stringify(body),
  };
};

export const getNoAuthFileCallParams = (body: any) => {
  return {
    method: "POST",
    headers: strings.multipartForm,
    body: body,
  };
};

export async function getHeaderObject(accessToken: string, contentType: any) {
  try {
    if (accessToken) {
      return {
        ...contentType,
        Authorization: accessToken,
      };
    }
    return null;
  } catch (error: any) {
    throw error;
  }
}

// Triggers the api call
// If the api call takes more time than timeout, then it will timeout the call.
export const makeCall = async (
  callName: string,
  callParams: any,
  convertToJSON: boolean = true,
) => {
  let statusCode;
  try {
    let call = fetch(callName, callParams);
    let timeout = getTimeoutPromise();

    const response: any = await Promise.race([timeout, call]).catch((error) => {
      throw error;
    });
    let json;
    statusCode = response.status;
    if (convertToJSON) {
      json = await response.json();
    }
    if (response && response.ok) {
      return json;
    }
    throw json;
  } catch (error: any) {
    error.statusCode = statusCode;
    throw error;
  }
};

export const getTimeoutPromise = () => {
  return new Promise((resolve, reject) => {
    setTimeout(
      () =>
        reject({
          error: true,
          message: "Something went wrong. Please reload the page.",
        }),
      40000, // 30 Seconds
    );
  });
};

export const fetchDocument = async (
  url: string,
  methodType: string,
  bodyObject?: any,
) => {
  let statusCode;
  try {
    const requestOption = await makeCall(methodType, bodyObject);
    const response = await fetch(url, requestOption);
    statusCode = response.status;
    if (response.ok) {
      return response.blob();
    } else {
      return response.text().then((text) => {
        const data = text && JSON.parse(text);
        const error = (data && data.errorMessage) || response.statusText;
        return Promise.reject(error);
      });
    }
  } catch (error: any) {
    error.statusCode = statusCode;
    error.errorMessage = handleErrorMessage(error);
    throw error;
  }
};

export const makeCallFile = async (callName: string, callParams: any) => {
  let statusCode;
  try {
    let call = fetch(callName, callParams);
    let timeout = getTimeoutPromise();
    const response: any = await Promise.race([timeout, call]).catch((err) => {
      throw err;
    });
    statusCode = response.status;
    if (response && response.ok) {
      return response.blob();
    } else {
      throw response.blob();
    }
  } catch (error: any) {
    error.statusCode = statusCode;
    error.errorMessage = handleErrorMessage(error);
    throw error;
  }
};

export async function checkIdToken(accessToken: string, refreshTok: string) {
  if (accessToken) {
    const decodedIdToken: any = jwtDecode(accessToken);
    const decodedRefreshToken: any = jwtDecode(refreshTok);
    let userEmail = decodedIdToken["ss:e"];

    if (decodedIdToken["exp"] * 1000 <= Date.now()) {
      if (userEmail && decodedRefreshToken["exp"] * 1000 < Date.now()) {
        store.dispatch(logOutAction({}));
        throw { error: true, message: "Refresh Token expired" };
      }
      try {
        const newUserIdToken = await refreshToken(refreshTok, userEmail);
        return newUserIdToken;
      } catch (error: any) {
        throw error;
      }
    }
    return accessToken;
  }
}

export async function refreshToken(accessToken: string, email: string) {
  try {
    const body = {
      refreshToken: accessToken,
      email,
    };
    const callParams = getNoAuthCallParams("POST", body);
    const response: any = await makeCall(urls.REFRESHTOKEN, callParams);
    store.dispatch(
      updateAccessToken({
        accessToken: response.authToken,
      }),
    );
    return response.authToken;
  } catch (error: any) {
    if (!isTruthy(error.message)) {
      throw {
        error: true,
        message: notifiers.GENERIC_ERROR,
      };
    }
    throw error;
  }
}
